import { ImgComparisonSlider } from "@img-comparison-slider/react";
import type { HTMLImgComparisonSliderElement } from "@img-comparison-slider/react";
import { motion } from "framer-motion";
import { X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { CSSProperties, SyntheticEvent } from "react";

import { useTranslation } from "@/core/i18n";

export type ImageSet = {
  beforeUrl: string;
  afterUrl: string;
  beforeLabel: string;
  afterLabel: string;
};

export type ImagePair = {
  key: string;
  label: string;
  icon: React.ReactNode;
  front: ImageSet;
  profile?: ImageSet;
};

type SliderModalProps = {
  pair: ImagePair;
  onClose: () => void;
};

export function SliderModal({ pair, onClose }: Readonly<SliderModalProps>) {
  const { t } = useTranslation();
  const sliderRef = useRef<HTMLImgComparisonSliderElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const [sliderPosition, setSliderPosition] = useState(50);
  const [activeView, setActiveView] = useState<"front" | "profile">("front");
  const [imageAspectRatio, setImageAspectRatio] = useState(3 / 4);
  const pointerStartRef = useRef<{ x: number; y: number } | null>(null);

  const hasProfile = Boolean(pair.profile);
  const imageSet = activeView === "profile" && pair.profile ? pair.profile : pair.front;

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    const el = sliderRef.current;
    if (!el?.shadowRoot) return;

    const attr = "data-patient-slider-fix";
    if (el.shadowRoot.querySelector(`[${attr}]`)) return;

    const s = document.createElement("style");
    s.setAttribute(attr, "true");
    s.textContent = ".second { height: 100%; } :host { cursor: ew-resize; }";
    el.shadowRoot.appendChild(s);

    return () => {
      s.remove();
    };
  }, [activeView]);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    pointerStartRef.current = { x: e.clientX, y: e.clientY };
  }, []);

  const handleClick = useCallback((e: React.MouseEvent) => {
    const start = pointerStartRef.current;
    pointerStartRef.current = null;
    if (!start) return;
    if (Math.abs(e.clientX - start.x) > 8 || Math.abs(e.clientY - start.y) > 8) return;

    const slider = sliderRef.current;
    if (!slider) return;
    const rect = slider.getBoundingClientRect();
    if (rect.width === 0) return;
    const percent = Math.round(((e.clientX - rect.left) / rect.width) * 100);
    const clamped = Math.max(0, Math.min(100, percent));
    slider.value = clamped;
    setSliderPosition(clamped);
  }, []);

  const handleViewSwitch = useCallback((view: "front" | "profile") => {
    setActiveView(view);
    setSliderPosition(50);
    setImageAspectRatio(3 / 4);
    if (sliderRef.current) {
      sliderRef.current.value = 50;
    }
  }, []);

  const handleImageLoad = useCallback((event: SyntheticEvent<HTMLImageElement>) => {
    const { naturalHeight, naturalWidth } = event.currentTarget;
    if (naturalWidth > 0 && naturalHeight > 0) {
      setImageAspectRatio(naturalWidth / naturalHeight);
    }
  }, []);

  const sliderStyle = {
    "--default-handle-color": "#6B7070",
    "--default-handle-width": "40px",
    "--divider-color": "rgba(255,255,255,0.5)",
    "--divider-width": "2px",
    "--handle-position-start": "75%",
    "--default-handle-shadow": "none",
    "outline": "none",
    "cursor": "ew-resize",
  } as CSSProperties;

  const beforeOpacity = getLabelOpacity(sliderPosition, "right");
  const afterOpacity = getLabelOpacity(sliderPosition, "left");
  const comparisonFrameStyle = {
    aspectRatio: imageAspectRatio,
    height: `min(100%, calc((100vw - 16px) / ${imageAspectRatio}))`,
    width: `min(100%, calc((100dvh - 140px) * ${imageAspectRatio}))`,
  } as CSSProperties;

  return (
    <motion.div
      className="fixed inset-0 z-50 flex flex-col bg-black"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      {/* eslint-disable-next-line react/no-danger */}
      <style
        dangerouslySetInnerHTML={{
          __html: `img-comparison-slider, img-comparison-slider:focus, img-comparison-slider:focus-within, img-comparison-slider:focus-visible { outline: none !important; box-shadow: none !important; border: none !important; -webkit-tap-highlight-color: transparent; }`,
        }}
      />
      <div className="flex items-center justify-between px-4 py-3">
        <h3 className="text-sm font-semibold text-white">{pair.label}</h3>
        <button
          ref={closeRef}
          type="button"
          className="rounded-full p-1 text-white/70 transition-colors hover:text-white"
          onClick={onClose}
          aria-label={t("common.close")}
        >
          <X className="h-6 w-6" />
        </button>
      </div>

      <div
        className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden px-2"
        role="region"
        aria-label={t("sliderModal.imageComparisonAriaLabel")}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
            e.preventDefault();
            const delta = e.key === "ArrowLeft" ? -5 : 5;
            const next = Math.max(0, Math.min(100, sliderPosition + delta));
            if (sliderRef.current) sliderRef.current.value = next;
            setSliderPosition(next);
          }
        }}
        onPointerDownCapture={handlePointerDown}
        tabIndex={0}
      >
        <div className="relative max-h-full max-w-full" style={comparisonFrameStyle}>
          <ImgComparisonSlider
            className="block h-full w-full overflow-hidden rounded-2xl focus:outline-none"
            handle={false}
            key={activeView}
            ref={sliderRef}
            style={sliderStyle}
            value={sliderPosition}
            onSlide={(e: React.ChangeEvent<HTMLImgComparisonSliderElement>) => {
              const val = Number(e.currentTarget.value ?? 50);
              if (Number.isFinite(val)) setSliderPosition(val);
            }}
          >
            <div
              className="flex items-center justify-center rounded-full bg-white/90 shadow-md"
              slot="handle"
              style={{ height: 40, width: 40 }}
            >
              <svg
                aria-hidden="true"
                className="h-4 w-6 text-gray-600"
                viewBox="-8 -3 16 6"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M -5 -2 L -7 0 L -5 2 M 5 -2 L 7 0 L 5 2"
                  fill="none"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.5"
                  vectorEffect="non-scaling-stroke"
                />
              </svg>
            </div>
            <img
              alt={imageSet.beforeLabel}
              className="block h-full w-full select-none object-contain"
              draggable={false}
              slot="first"
              src={imageSet.beforeUrl}
              onLoad={handleImageLoad}
            />
            <img
              alt={imageSet.afterLabel}
              className="block h-full w-full select-none object-contain"
              draggable={false}
              slot="second"
              src={imageSet.afterUrl}
            />
          </ImgComparisonSlider>

          <div
            className="pointer-events-none absolute left-3 top-3 rounded-md bg-black/55 px-2 py-1 backdrop-blur-sm transition-opacity duration-75"
            style={{ opacity: beforeOpacity }}
          >
            <span className="text-xs font-bold text-white/85">{imageSet.beforeLabel}</span>
          </div>
          <div
            className="pointer-events-none absolute right-3 top-3 rounded-md bg-black/55 px-2 py-1 backdrop-blur-sm transition-opacity duration-75"
            style={{ opacity: afterOpacity }}
          >
            <span className="text-xs font-bold text-white/85">{imageSet.afterLabel}</span>
          </div>
        </div>
      </div>

      <div className="flex flex-col items-center gap-3 px-4 py-3">
        {hasProfile ? (
          <div className="flex rounded-full bg-white/15 p-1 backdrop-blur-sm">
            {(["front", "profile"] as const).map((view) => (
              <button
                className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${
                  activeView === view ? "bg-white text-black" : "text-white/80 hover:text-white"
                }`}
                key={view}
                type="button"
                aria-pressed={activeView === view}
                onClick={() => handleViewSwitch(view)}
              >
                {view === "front" ? t("sliderModal.frontView") : t("sliderModal.profileView")}
              </button>
            ))}
          </div>
        ) : null}
        <p className="text-xs text-white/50">{t("sliderModal.dragToCompare")}</p>
      </div>
    </motion.div>
  );
}

function getLabelOpacity(position: number, side: "left" | "right"): number {
  const clamped = Math.max(0, Math.min(100, position));
  const distance = side === "left" ? 100 - clamped : clamped;
  if (distance <= 2) return 0;
  if (distance >= 50) return 1;
  const progress = (distance - 2) / 48;
  return Math.round(progress * progress * (3 - 2 * progress) * 100) / 100;
}
